import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

// NICE brand colors
const NICE_BLUE = '#003594';
const GRAY_TEXT = '#666666';
const LIGHT_GRAY = '#f5f5f5';

// Logo paths - try local first, fallback to CDN
const LOCAL_LOGO_PATH = '/images/nice-logo.jpg';
const CDN_LOGO_URL = 'https://customer-assets.emergentagent.com/job_nice-grader/artifacts/vj0kbjm0_Nice%20logo%200925%20%283%29.jpg';

/**
 * Load image and convert to base64 for jsPDF
 * @param {string} url - Image URL
 * @returns {Promise<string>} - Base64 data URL
 */
const loadImageAsBase64 = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      const dataURL = canvas.toDataURL('image/jpeg', 0.9);
      resolve(dataURL);
    };
    img.onerror = () => {
      // If image fails to load, resolve with null (will fall back to text)
      resolve(null);
    };
    img.src = url;
  });
};

/**
 * Try to load logo from local path first, then CDN
 * @returns {Promise<string|null>} - Base64 data URL or null
 */
const loadLogo = async () => {
  // Try local path first
  let logoBase64 = await loadImageAsBase64(LOCAL_LOGO_PATH);
  if (logoBase64) return logoBase64;
  
  // Fallback to CDN
  logoBase64 = await loadImageAsBase64(CDN_LOGO_URL);
  return logoBase64;
};

/**
 * Generate a PDF packing slip for a submission
 * @param {Object} submission - The submission data
 * @param {Object} user - The user data
 * @param {Array} cards - Array of card details (optional)
 * @param {Object} shippingAddress - The shipping address
 * @returns {Promise<string>} - The filename of the generated PDF
 */
export const generatePackingSlipPDF = async (submission, user, cards = [], shippingAddress) => {
  try {
    // Create new PDF document (Letter size)
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'letter'
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    let yPos = margin;

    // Helper function to add text
    const addText = (text, x, y, options = {}) => {
      const { fontSize = 10, fontStyle = 'normal', color = '#000000', align = 'left' } = options;
      doc.setFontSize(fontSize);
      doc.setFont('helvetica', fontStyle);
      
      // Parse hex color to RGB
      const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16)
        } : { r: 0, g: 0, b: 0 };
      };
      
      const rgb = hexToRgb(color);
      doc.setTextColor(rgb.r, rgb.g, rgb.b);
      doc.text(String(text || ''), x, y, { align });
    };

    // Helper function to draw a line
    const drawLine = (x1, y1, x2, y2, color = NICE_BLUE) => {
      const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16)
        } : { r: 0, g: 0, b: 0 };
      };
      const rgb = hexToRgb(color);
      doc.setDrawColor(rgb.r, rgb.g, rgb.b);
      doc.setLineWidth(0.5);
      doc.line(x1, y1, x2, y2);
    };

    // Helper function to draw a rectangle
    const drawRect = (x, y, w, h, fillColor = null, strokeColor = NICE_BLUE) => {
      const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16)
        } : { r: 0, g: 0, b: 0 };
      };
      
      if (fillColor) {
        const rgb = hexToRgb(fillColor);
        doc.setFillColor(rgb.r, rgb.g, rgb.b);
        doc.rect(x, y, w, h, 'F');
      }
      if (strokeColor) {
        const rgb = hexToRgb(strokeColor);
        doc.setDrawColor(rgb.r, rgb.g, rgb.b);
        doc.setLineWidth(0.3);
        doc.rect(x, y, w, h, 'S');
      }
    };

    // ==================== HEADER SECTION ====================
    
    // Try to load and add NICE logo image
    let logoLoaded = false;
    try {
      const logoBase64 = await loadLogo();
      if (logoBase64) {
        // Add logo image with correct aspect ratio (2:1 based on actual image 375x187)
        // Logo width: 35mm, height: 17.5mm to maintain aspect ratio
        const logoWidth = 35;
        const logoHeight = 17.5;
        doc.addImage(logoBase64, 'JPEG', margin, yPos, logoWidth, logoHeight);
        logoLoaded = true;
      }
    } catch (e) {
      console.warn('Failed to load logo, using text fallback:', e);
    }
    
    // Fallback to text if image didn't load
    if (!logoLoaded) {
      addText('NICE', margin, yPos + 10, { fontSize: 28, fontStyle: 'bold', color: NICE_BLUE });
      addText('GRADING', margin + 32, yPos + 10, { fontSize: 28, fontStyle: 'normal', color: '#333333' });
    }

    // Title on the right
    addText('GRADING SUBMISSION', pageWidth - margin, yPos + 5, { 
      fontSize: 16, 
      fontStyle: 'bold', 
      color: NICE_BLUE, 
      align: 'right' 
    });
    addText('Include this form with your cards', pageWidth - margin, yPos + 12, { 
      fontSize: 10, 
      color: GRAY_TEXT, 
      align: 'right' 
    });

    yPos += 22; // Account for logo height (17.5mm) + spacing
    drawLine(margin, yPos, pageWidth - margin, yPos, NICE_BLUE);
    yPos += 10;

    // ==================== SUBMISSION DETAILS SECTION ====================
    
    // Submission number box (prominent)
    drawRect(margin, yPos, pageWidth - 2 * margin, 25, LIGHT_GRAY, NICE_BLUE);
    addText('SUBMISSION NUMBER', margin + 5, yPos + 7, { fontSize: 9, color: GRAY_TEXT });
    addText(submission?.submission_number || 'N/A', margin + 5, yPos + 18, { 
      fontSize: 18, 
      fontStyle: 'bold', 
      color: NICE_BLUE 
    });
    
    // Date on the right side of the box
    addText('Date Submitted', pageWidth - margin - 5, yPos + 7, { 
      fontSize: 9, 
      color: GRAY_TEXT, 
      align: 'right' 
    });
    
    let formattedDate = 'N/A';
    try {
      formattedDate = new Date(submission?.created_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      formattedDate = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
    
    addText(formattedDate, pageWidth - margin - 5, yPos + 18, { 
      fontSize: 12, 
      fontStyle: 'bold', 
      align: 'right' 
    });

    yPos += 35;

    // Customer info row
    const colWidth = (pageWidth - 2 * margin) / 2;
    
    // Customer Name
    addText('CUSTOMER NAME', margin, yPos, { fontSize: 9, color: GRAY_TEXT });
    addText(user?.name || 'N/A', margin, yPos + 6, { fontSize: 11, fontStyle: 'bold' });
    
    // Customer Email
    addText('CUSTOMER EMAIL', margin + colWidth, yPos, { fontSize: 9, color: GRAY_TEXT });
    addText(user?.email || 'N/A', margin + colWidth, yPos + 6, { fontSize: 11, fontStyle: 'bold' });

    yPos += 18;
    drawLine(margin, yPos, pageWidth - margin, yPos, '#dddddd');
    yPos += 10;

    // ==================== RETURN ADDRESS SECTION ====================
    
    addText('RETURN SHIPPING ADDRESS', margin, yPos, { fontSize: 11, fontStyle: 'bold', color: NICE_BLUE });
    yPos += 8;

    if (shippingAddress) {
      addText(shippingAddress.name || '', margin, yPos, { fontSize: 10 });
      yPos += 5;
      addText(shippingAddress.street_address || '', margin, yPos, { fontSize: 10 });
      yPos += 5;
      addText(`${shippingAddress.city || ''}, ${shippingAddress.state || ''} ${shippingAddress.zip_code || ''}`, margin, yPos, { fontSize: 10 });
      yPos += 5;
      if (shippingAddress.phone) {
        addText(`Phone: ${shippingAddress.phone}`, margin, yPos, { fontSize: 10 });
        yPos += 5;
      }
    } else {
      addText('No return address provided', margin, yPos, { fontSize: 10, color: GRAY_TEXT });
      yPos += 5;
    }

    yPos += 10;
    drawLine(margin, yPos, pageWidth - margin, yPos, '#dddddd');
    yPos += 10;

    // ==================== SUBMISSION SUMMARY ====================
    
    addText('SUBMISSION SUMMARY', margin, yPos, { fontSize: 11, fontStyle: 'bold', color: NICE_BLUE });
    yPos += 10;

    // Summary data - use dynamic values from database
    const cardCount = submission?.card_count || 0;
    const pricePerCard = submission?.price_per_card || 0;
    const gradingCost = submission?.grading_cost || (cardCount * pricePerCard);
    const shippingCost = submission?.shipping_cost ?? 15;
    const insuranceCost = submission?.insurance_cost ?? 0;
    const packageWeight = submission?.package_weight_lbs || 0;
    const orderTotal = submission?.total_price || (gradingCost + shippingCost + insuranceCost);
    const estimatedValue = submission?.estimated_submission_value || 0;

    // Format currency with commas
    const formatCurrency = (val) => {
      if (val === 0) return '$0';
      return `$${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const summaryData = [
      ['Total Cards:', `${cardCount} cards`],
      ['Price Per Card:', formatCurrency(pricePerCard)],
      ['Grading Total:', formatCurrency(gradingCost)],
    ];

    // Add shipping with weight info
    if (packageWeight > 0) {
      summaryData.push(['Return Shipping:', formatCurrency(shippingCost)]);
      summaryData.push(['  Package Weight:', `${packageWeight} lbs`]);
    } else {
      summaryData.push(['Return Shipping:', formatCurrency(shippingCost)]);
    }

    // Add insurance
    if (insuranceCost > 0) {
      summaryData.push(['Insurance:', formatCurrency(insuranceCost)]);
      if (estimatedValue > 0) {
        summaryData.push(['  Coverage up to:', `$${Number(estimatedValue).toLocaleString()}`]);
      }
    } else if (estimatedValue > 0 && estimatedValue <= 100) {
      summaryData.push(['Insurance:', 'Included']);
    }

    // Add order total
    summaryData.push(['Order Total:', formatCurrency(orderTotal)]);

    // Add estimated value if present
    if (estimatedValue > 0) {
      summaryData.push(['Declared Value:', `$${Number(estimatedValue).toLocaleString()}`]);
    }

    summaryData.forEach(([label, value]) => {
      const isTotal = label === 'Order Total:';
      const isSubInfo = label.startsWith('  ');
      addText(isSubInfo ? label.trim() : label, margin + (isSubInfo ? 5 : 0), yPos, { 
        fontSize: isTotal ? 11 : (isSubInfo ? 9 : 10), 
        fontStyle: isTotal ? 'bold' : 'normal',
        color: isTotal ? NICE_BLUE : (isSubInfo ? GRAY_TEXT : '#000000')
      });
      addText(value, margin + 65, yPos, { 
        fontSize: isTotal ? 11 : (isSubInfo ? 9 : 10), 
        fontStyle: isTotal ? 'bold' : 'normal',
        color: isTotal ? NICE_BLUE : (isSubInfo ? GRAY_TEXT : '#000000')
      });
      yPos += isSubInfo ? 5 : 6;
    });

    yPos += 8;
    drawLine(margin, yPos, pageWidth - margin, yPos, '#dddddd');
    yPos += 10;

    // ==================== CARD DETAILS TABLE ====================
    
    addText('CARD DETAILS', margin, yPos, { fontSize: 11, fontStyle: 'bold', color: NICE_BLUE });
    yPos += 8;

    if (cards && cards.length > 0) {
      // Use autoTable for card details
      const tableData = cards.map((card, index) => [
        index + 1,
        card.year || '-',
        card.set_brand || '-',
        card.card_number || '-',
        card.player_name || '-'
      ]);

      doc.autoTable({
        startY: yPos,
        head: [['#', 'Year', 'Set/Brand', 'Card #', 'Player/Subject']],
        body: tableData,
        margin: { left: margin, right: margin },
        styles: {
          fontSize: 9,
          cellPadding: 2,
        },
        headStyles: {
          fillColor: [0, 53, 148], // NICE_BLUE
          textColor: [255, 255, 255],
          fontStyle: 'bold',
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245],
        },
        columnStyles: {
          0: { cellWidth: 10 },
          1: { cellWidth: 20 },
          2: { cellWidth: 50 },
          3: { cellWidth: 25 },
          4: { cellWidth: 50 },
        },
      });

      yPos = doc.lastAutoTable.finalY + 10;
    } else {
      drawRect(margin, yPos, pageWidth - 2 * margin, 15, LIGHT_GRAY, null);
      addText('Card details will be researched upon receipt', margin + 5, yPos + 10, { 
        fontSize: 10, 
        fontStyle: 'italic', 
        color: GRAY_TEXT 
      });
      yPos += 25;
    }

    // ==================== SHIPPING INSTRUCTIONS ====================
    
    // Check if we need a new page
    if (yPos > pageHeight - 100) {
      doc.addPage();
      yPos = margin;
    }

    // ==================== SHIPPING ADDRESSES ====================
    
    addText('SHIP YOUR CARDS TO:', margin, yPos, { fontSize: 11, fontStyle: 'bold', color: NICE_BLUE });
    yPos += 8;
    addText('Use the address that matches your shipping carrier. Include this packing slip inside your package.', margin, yPos, { 
      fontSize: 9, 
      color: GRAY_TEXT 
    });
    yPos += 10;

    const boxWidth = (pageWidth - 2 * margin - 10) / 2;
    const boxHeight = 55;
    
    // USPS Address Box (Blue)
    drawRect(margin, yPos, boxWidth, boxHeight, '#dbeafe', '#2563eb');
    addText('USPS', margin + 5, yPos + 8, { fontSize: 10, fontStyle: 'bold', color: '#1e40af' });
    addText('NICE Grading', margin + 5, yPos + 18, { fontSize: 10, fontStyle: 'bold' });
    addText('Attn: Receiving', margin + 5, yPos + 25, { fontSize: 9 });
    addText('P.O. Box 30041', margin + 5, yPos + 32, { fontSize: 9 });
    addText('Rochester, NY 14603', margin + 5, yPos + 39, { fontSize: 9 });
    
    // UPS/FedEx Address Box (Amber)
    const rightBoxX = margin + boxWidth + 10;
    drawRect(rightBoxX, yPos, boxWidth, boxHeight, '#fef3c7', '#d97706');
    addText('UPS / FedEx', rightBoxX + 5, yPos + 8, { fontSize: 10, fontStyle: 'bold', color: '#92400e' });
    addText('NICE Grading', rightBoxX + 5, yPos + 18, { fontSize: 10, fontStyle: 'bold' });
    addText('Attn: Receiving', rightBoxX + 5, yPos + 25, { fontSize: 9 });
    addText('250 Cumberland St., Ste. 202', rightBoxX + 5, yPos + 32, { fontSize: 9 });
    addText('Rochester, NY 14605', rightBoxX + 5, yPos + 39, { fontSize: 9 });
    
    yPos += boxHeight + 8;
    
    addText(`Reference: Submission #${submission?.submission_number || 'N/A'}`, pageWidth / 2, yPos, { 
      fontSize: 11, 
      fontStyle: 'bold', 
      color: NICE_BLUE,
      align: 'center'
    });

    yPos += 15;

    // ==================== FOOTER ====================
    
    // Add footer at bottom of page
    const footerY = pageHeight - 20;
    drawLine(margin, footerY - 5, pageWidth - margin, footerY - 5, '#dddddd');
    
    addText('Questions? Contact us at support@nicegrading.com', pageWidth / 2, footerY, { 
      fontSize: 9, 
      color: GRAY_TEXT, 
      align: 'center' 
    });
    addText('Track your submission at nicegrading.com/dashboard', pageWidth / 2, footerY + 5, { 
      fontSize: 9, 
      color: GRAY_TEXT, 
      align: 'center' 
    });

    // Save the PDF
    const filename = `NICE-${submission?.submission_number || 'Unknown'}-Packing-Slip.pdf`;
    doc.save(filename);
    
    return filename;
  } catch (error) {
    console.error('PDF Generation Error:', error);
    throw new Error(`Failed to generate PDF: ${error.message}`);
  }
};

/**
 * Generate printable HTML content as fallback
 * @param {Object} submission - The submission data
 * @param {Object} user - The user data
 * @param {Array} cards - Array of card details (optional)
 * @param {Object} shippingAddress - The shipping address
 */
export const generatePrintableHTML = (submission, user, cards = [], shippingAddress) => {
  // Use dynamic values from database
  const cardCount = submission?.card_count || 0;
  const pricePerCard = submission?.price_per_card || 0;
  const gradingCost = submission?.grading_cost || (cardCount * pricePerCard);
  const shippingCost = submission?.shipping_cost ?? 15;
  const insuranceCost = submission?.insurance_cost ?? 0;
  const packageWeight = submission?.package_weight_lbs || 0;
  const orderTotal = submission?.total_price || (gradingCost + shippingCost + insuranceCost);
  const estimatedValue = submission?.estimated_submission_value || 0;

  // Format currency with commas
  const formatCurrency = (val) => {
    if (val === 0) return '$0';
    return `$${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  
  let formattedDate = 'N/A';
  try {
    formattedDate = new Date(submission?.created_at).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (e) {
    formattedDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  const cardsHTML = cards && cards.length > 0 
    ? `<table style="width: 100%; border-collapse: collapse; margin-top: 10px;">
        <thead>
          <tr style="background: #003594; color: white;">
            <th style="padding: 8px; text-align: left; border: 1px solid #003594;">#</th>
            <th style="padding: 8px; text-align: left; border: 1px solid #003594;">Year</th>
            <th style="padding: 8px; text-align: left; border: 1px solid #003594;">Set/Brand</th>
            <th style="padding: 8px; text-align: left; border: 1px solid #003594;">Card #</th>
            <th style="padding: 8px; text-align: left; border: 1px solid #003594;">Player/Subject</th>
          </tr>
        </thead>
        <tbody>
          ${cards.map((card, i) => `
            <tr style="background: ${i % 2 === 0 ? '#f5f5f5' : 'white'};">
              <td style="padding: 8px; border: 1px solid #ddd;">${i + 1}</td>
              <td style="padding: 8px; border: 1px solid #ddd;">${card.year || '-'}</td>
              <td style="padding: 8px; border: 1px solid #ddd;">${card.set_brand || '-'}</td>
              <td style="padding: 8px; border: 1px solid #ddd;">${card.card_number || '-'}</td>
              <td style="padding: 8px; border: 1px solid #ddd;">${card.player_name || '-'}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>`
    : '<p style="color: #666; font-style: italic; padding: 15px; background: #f5f5f5; border-radius: 8px;">Card details will be researched upon receipt</p>';

  // Build insurance row HTML
  let insuranceHTML = '';
  if (insuranceCost > 0) {
    insuranceHTML = `
      <div class="summary-row"><span>Insurance:</span><span>${formatCurrency(insuranceCost)}</span></div>
      ${estimatedValue > 0 ? `<div class="summary-row sub-info"><span>Coverage up to:</span><span>$${Number(estimatedValue).toLocaleString()}</span></div>` : ''}
    `;
  } else if (estimatedValue > 0 && estimatedValue <= 100) {
    insuranceHTML = `<div class="summary-row"><span>Insurance:</span><span>Included</span></div>`;
  }

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Packing Slip - ${submission?.submission_number || 'Unknown'}</title>
      <style>
        @media print {
          body { margin: 0; padding: 20px; }
          .no-print { display: none !important; }
        }
        body { font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px; color: #333; }
        .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #003594; padding-bottom: 15px; margin-bottom: 20px; }
        .logo img { height: 50px; width: auto; }
        .title { text-align: right; }
        .title h1 { color: #003594; font-size: 18px; margin: 0; }
        .title p { color: #666; font-size: 12px; margin: 5px 0 0 0; }
        .submission-box { background: #f5f5f5; border: 2px solid #003594; border-radius: 8px; padding: 15px; margin-bottom: 20px; display: flex; justify-content: space-between; }
        .submission-number { }
        .submission-number label { font-size: 11px; color: #666; display: block; }
        .submission-number .number { font-size: 24px; font-weight: bold; color: #003594; }
        .date { text-align: right; }
        .date label { font-size: 11px; color: #666; display: block; }
        .date .value { font-size: 14px; font-weight: bold; }
        .section { margin-bottom: 20px; }
        .section h3 { color: #003594; font-size: 14px; margin-bottom: 10px; border-bottom: 1px solid #ddd; padding-bottom: 5px; }
        .customer-info { display: flex; gap: 40px; }
        .customer-info div label { font-size: 11px; color: #666; display: block; }
        .customer-info div p { font-weight: bold; margin: 5px 0 0 0; }
        .summary-row { display: flex; justify-content: space-between; padding: 5px 0; }
        .summary-row.sub-info { font-size: 12px; color: #666; padding: 2px 0 2px 15px; }
        .summary-row.total { font-weight: bold; color: #003594; font-size: 14px; border-top: 1px solid #ddd; margin-top: 5px; padding-top: 10px; }
        .summary-row.declared { border-top: 1px solid #ddd; margin-top: 5px; padding-top: 10px; }
        .ship-to { background: #e8f0fe; border: 2px solid #003594; border-radius: 8px; padding: 15px; }
        .ship-to h3 { color: #003594; margin: 0 0 10px 0; }
        .ship-to .attention { color: #003594; font-weight: bold; margin-top: 10px; }
        .footer { text-align: center; color: #666; font-size: 12px; border-top: 1px solid #ddd; padding-top: 15px; margin-top: 30px; }
        .print-btn { background: #003594; color: white; border: none; padding: 12px 24px; font-size: 16px; cursor: pointer; border-radius: 8px; margin: 20px auto; display: block; }
        .print-btn:hover { background: #002554; }
      </style>
    </head>
    <body>
      <button class="print-btn no-print" onclick="window.print()">🖨️ Print This Page</button>
      
      <div class="header">
        <div class="logo">
          <img src="${LOCAL_LOGO_PATH}" alt="NICE Grading" onerror="this.style.display='none'; this.nextElementSibling.style.display='block';" />
          <div style="display: none; font-size: 28px; font-weight: bold;"><span style="color: #003594;">NICE</span> <span style="font-weight: normal; color: #333;">GRADING</span></div>
        </div>
        <div class="title">
          <h1>CARD GRADING SUBMISSION</h1>
          <p>Include this form with your cards</p>
        </div>
      </div>

      <div class="submission-box">
        <div class="submission-number">
          <label>SUBMISSION NUMBER</label>
          <div class="number">${submission?.submission_number || 'N/A'}</div>
        </div>
        <div class="date">
          <label>Date Submitted</label>
          <div class="value">${formattedDate}</div>
        </div>
      </div>

      <div class="section">
        <div class="customer-info">
          <div>
            <label>CUSTOMER NAME</label>
            <p>${user?.name || 'N/A'}</p>
          </div>
          <div>
            <label>CUSTOMER EMAIL</label>
            <p>${user?.email || 'N/A'}</p>
          </div>
        </div>
      </div>

      <div class="section">
        <h3>RETURN SHIPPING ADDRESS</h3>
        ${shippingAddress ? `
          <p style="margin: 0;">${shippingAddress.name || ''}</p>
          <p style="margin: 0;">${shippingAddress.street_address || ''}</p>
          <p style="margin: 0;">${shippingAddress.city || ''}, ${shippingAddress.state || ''} ${shippingAddress.zip_code || ''}</p>
          ${shippingAddress.phone ? `<p style="margin: 0;">Phone: ${shippingAddress.phone}</p>` : ''}
        ` : '<p style="color: #666;">No return address provided</p>'}
      </div>

      <div class="section">
        <h3>SUBMISSION SUMMARY</h3>
        <div class="summary-row"><span>Total Cards:</span><span>${cardCount} cards</span></div>
        <div class="summary-row"><span>Price Per Card:</span><span>${formatCurrency(pricePerCard)}</span></div>
        <div class="summary-row"><span>Grading Total:</span><span>${formatCurrency(gradingCost)}</span></div>
        <div class="summary-row"><span>Return Shipping:</span><span>${formatCurrency(shippingCost)}</span></div>
        ${packageWeight > 0 ? `<div class="summary-row sub-info"><span>Package weight:</span><span>${packageWeight} lbs</span></div>` : ''}
        ${insuranceHTML}
        <div class="summary-row total"><span>Order Total:</span><span>${formatCurrency(orderTotal)}</span></div>
        ${estimatedValue > 0 ? `<div class="summary-row declared"><span>Declared Value:</span><span>$${Number(estimatedValue).toLocaleString()}</span></div>` : ''}
      </div>

      <div class="section">
        <h3>CARD DETAILS</h3>
        ${cardsHTML}
      </div>

      <div class="ship-to" style="padding: 20px;">
        <h3 style="margin-bottom: 15px;">📦 SHIP YOUR CARDS TO:</h3>
        <p style="margin: 0 0 15px 0; font-size: 12px; color: #666;">Use the address that matches your shipping carrier. Include this packing slip inside your package.</p>
        
        <div style="display: flex; gap: 15px;">
          <!-- USPS Address -->
          <div style="flex: 1; background: #dbeafe; border: 2px solid #2563eb; border-radius: 8px; padding: 12px;">
            <p style="margin: 0 0 8px 0; font-weight: bold; color: #1e40af;">📮 USPS</p>
            <p style="margin: 0; font-weight: bold;">NICE Grading</p>
            <p style="margin: 0;">Attn: Receiving</p>
            <p style="margin: 0;">P.O. Box 30041</p>
            <p style="margin: 0;">Rochester, NY 14603</p>
          </div>
          
          <!-- UPS/FedEx Address -->
          <div style="flex: 1; background: #fef3c7; border: 2px solid #d97706; border-radius: 8px; padding: 12px;">
            <p style="margin: 0 0 8px 0; font-weight: bold; color: #92400e;">🚚 UPS / FedEx</p>
            <p style="margin: 0; font-weight: bold;">NICE Grading</p>
            <p style="margin: 0;">Attn: Receiving</p>
            <p style="margin: 0;">250 Cumberland St.</p>
            <p style="margin: 0;">Ste. 202</p>
            <p style="margin: 0;">Rochester, NY 14605</p>
          </div>
        </div>
        
        <p class="attention" style="text-align: center; margin-top: 15px;">Reference: Submission #${submission?.submission_number || 'N/A'}</p>
      </div>

      <div class="footer">
        <p>Questions? Contact us at support@nicegrading.com</p>
        <p>Track your submission at nicegrading.com/dashboard</p>
      </div>
    </body>
    </html>
  `;
};

/**
 * Open printable version in new window
 */
export const openPrintableVersion = (submission, user, cards = [], shippingAddress) => {
  const html = generatePrintableHTML(submission, user, cards, shippingAddress);
  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(html);
    printWindow.document.close();
  } else {
    throw new Error('Could not open print window. Please allow popups for this site.');
  }
};

export default generatePackingSlipPDF;
